import { useMemo } from 'react';
import { D, type HistoryRange } from '@wave/shared';
import { EChart, type EChartsOption } from '../EChart';
import { useIsNarrow } from '../../lib/useMediaQuery';
import { useBalanceHistoryByChat } from './useBalanceHistory';
import { BASE_GRID, CHART_COLORS, tooltipFit, valueAxisLabel, EmptyHistory } from './ChartsPanel';

/**
 * Топ-10 чатов по |Δ net USDT| за период (последняя точка − первая).
 * Δ>0 — «мы должны» выросло — красный; Δ<0 — зелёный. Клик — история чата.
 */
export function MoversChart({
  range,
  onOpenChat,
}: {
  range: HistoryRange;
  onOpenChat: (chatId: string, title: string) => void;
}) {
  const { data, isLoading } = useBalanceHistoryByChat(range);
  const narrow = useIsNarrow();

  const movers = useMemo(() => {
    const rows = (data?.chats ?? [])
      // Топ изменений — про контрагентов; динамика кошельков (активы) видна
      // отдельной линией «Кошельки» на сводном тренде, здесь их не смешиваем.
      .filter((c) => c.source !== 'tron')
      .filter((c) => c.points.length >= 2)
      .map((c) => ({
        chatId: c.chatId,
        title: c.title,
        delta: D(c.points[c.points.length - 1]!.netUsdt).minus(D(c.points[0]!.netUsdt)),
      }))
      .filter((r) => !r.delta.isZero());
    rows.sort((a, b) => b.delta.abs().comparedTo(a.delta.abs()));
    return rows.slice(0, 10).reverse(); // reverse: ECharts рисует категории снизу вверх
  }, [data]);

  const option = useMemo<EChartsOption>(
    () => ({
      animation: false,
      grid: narrow ? { left: 96, right: 12, top: 16, bottom: 32 } : { ...BASE_GRID, left: 160 },
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        ...tooltipFit(narrow),
        formatter: barTooltip(movers.map((m) => m.title)),
      },
      xAxis: { type: 'value', axisLabel: valueAxisLabel(narrow), splitLine: { lineStyle: { color: CHART_COLORS.grid } } },
      yAxis: {
        type: 'category',
        data: movers.map((m) => catLabel(m.title, narrow)),
        axisLabel: { color: CHART_COLORS.text, ...(narrow ? { fontSize: 11 } : {}) },
      },
      series: [
        {
          type: 'bar',
          data: movers.map((m) => ({
            value: m.delta.toDecimalPlaces(2).toNumber(),
            itemStyle: { color: m.delta.gt(0) ? CHART_COLORS.pos : CHART_COLORS.neg },
          })),
          barMaxWidth: 18,
        },
      ],
    }),
    [movers, narrow],
  );

  if (isLoading) return <div className="h-40 animate-pulse rounded bg-bg3" />;
  if (movers.length === 0) return <EmptyHistory pointsCount={movers.length} />;
  return (
    <EChart
      option={option}
      height={Math.max(200, movers.length * 34 + 90)}
      onEvents={{
        click: (params) => {
          const idx = (params as { dataIndex?: number }).dataIndex;
          if (idx === undefined) return;
          const m = movers[idx];
          if (m) onOpenChat(m.chatId, m.title);
        },
      }}
    />
  );
}

/** Подпись категории: на телефоне короче — поле под подписи уже (полное имя — в тултипе). */
function catLabel(title: string, narrow: boolean): string {
  const max = narrow ? 12 : 24;
  return title.length > max ? `${title.slice(0, max)}…` : title;
}

/** Тултип бара: полное имя чата + значение (подпись оси может быть обрезана). */
function barTooltip(titles: string[]) {
  return (params: unknown) => {
    const p = (params as Array<{ dataIndex: number; value: number; marker: string }>)[0];
    if (!p) return '';
    const v = p.value.toLocaleString('ru-RU', { maximumFractionDigits: 2 });
    return `${titles[p.dataIndex] ?? ''}<br/>${p.marker} <b>${p.value > 0 ? '+' : ''}${v}</b> USDT`;
  };
}
